import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ChevronDown, ChevronUp, Download, Link2, Loader2, Paperclip } from "lucide-react";
import api, { apiErrorMessage } from "../../lib/api";
import { downloadBlob } from "../../lib/downloadBlob";
import { asciiName, versionLine } from "../../lib/portalPresence";
import { calendarRows, checksFrom, gridState, rowState, whenLabel } from "../../lib/portalPortfolio";
import { Chip } from "./PortalShell";
import { downloadFile } from "./SubmissionHistory";

const card = "rounded-2xl border border-[#E5E7EB] bg-white";
const quiet = "inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] bg-white px-3 py-1.5 text-xs font-semibold text-[#373A45] hover:border-[#965EA7] hover:text-[#965EA7] disabled:opacity-50";
const primary = "inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] px-4 py-2 text-sm font-semibold text-white hover:bg-[#7A4986] disabled:opacity-50";
const field = "w-full rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 text-sm text-[#373A45] focus:border-[#965EA7] focus:outline-none focus:ring-2 focus:ring-[#965EA7]/20";

/**
 * The group's portfolios and their structure check — the same board for the
 * trainer (`base="/portal"`) and the team (`base="/admin/portal"`, `team`):
 * the calendar, who handed in what, the seven-element grid with what the
 * platform found next to each element, and Anexa 6. The team also sets the
 * deadline and approves a second hand-in.
 */
export default function PortfolioBoard({ base, groupId, team = false }) {
  const [kind, setKind] = useState("main");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      setData((await api.get(`${base}/groups/${groupId}/portfolios`, { params: { session: kind } })).data);
      setError("");
    } catch (err) {
      setError(apiErrorMessage(err, "Nu am putut încărca portofoliile."));
    }
  }, [base, groupId, kind]);
  useEffect(() => { load(); }, [load]);

  if (error) return <p className="text-sm text-[#c0483f]" role="alert">{error}</p>;
  if (!data) return <div className="py-10 text-center text-[#6B7280]"><Loader2 className="mx-auto animate-spin" /></div>;
  const s = data.session;
  const hasResit = data.sessions.some((x) => x.kind === "resit");
  const c = data.counts;

  return (
    <div className="grid gap-8" data-testid="portfolio-board">
      {hasResit && (
        <div className="inline-flex w-fit overflow-hidden rounded-full border border-[#E5E7EB] bg-white" role="tablist">
          {[["main", "Sesiunea principală"], ["resit", "Sesiunea de restanță"]].map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={kind === k} onClick={() => { setKind(k); setOpen(null); }}
              className={`px-4 py-1.5 text-sm font-semibold ${kind === k ? "bg-[#373A45] text-white" : "text-[#373A45] hover:bg-[#F5F6F6]"}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      <section aria-labelledby="pf-calendar">
        <h2 id="pf-calendar" className="font-display text-xl font-bold text-[#373A45]">Calendarul evaluării</h2>
        {!s && <p className="mt-2 text-sm text-[#6B7280]">Grupa nu are sesiuni, deci nici termene.</p>}
        {s && (
          <>
            <ul className={`mt-3 divide-y divide-[#E5E7EB] ${card}`}>
              {calendarRows(s).map((r) => (
                <li key={r.key} className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-2.5 text-sm">
                  <span className="text-[#373A45]">{r.what} <span className="text-xs text-[#6B7280]">· {r.who}</span></span>
                  <span className="font-semibold text-[#373A45]">{r.when} <span className="text-xs font-normal text-[#6B7280]">({r.rule})</span></span>
                </li>
              ))}
            </ul>
            {(s.warnings || []).map((w) => (
              <p key={w} className="mt-2 flex gap-2 rounded-xl bg-[#d99a2b]/10 px-3 py-2 text-sm text-[#8a5d12]">
                <AlertTriangle size={15} className="mt-0.5 shrink-0" /> {w}
              </p>
            ))}
            {team && <DeadlineEditor session={s} groupId={groupId} kind={kind} defaultDays={data.default_days} onDone={load} />}
          </>
        )}
      </section>

      <section aria-labelledby="pf-rows">
        <h2 id="pf-rows" className="font-display text-xl font-bold text-[#373A45]">Portofoliile</h2>
        <p className="mt-1 text-sm text-[#6B7280]" data-testid="pf-counts">
          {c.verified} verificate · {c.submitted} de verificat · {c.draft} în lucru · {c.empty} neîncepute
          {c.not_submitted ? ` · ${c.not_submitted} netransmise` : ""}
        </p>
        <div className={`mt-3 overflow-x-auto ${card}`}>
          <table className="w-full min-w-[34rem] text-sm">
            <thead>
              <tr className="border-b border-[#E5E7EB] text-left text-xs text-[#6B7280]">
                <th className="px-4 py-2.5 font-semibold">Cursant</th>
                <th className="px-3 py-2.5 font-semibold">Stare</th>
                <th className="px-3 py-2.5 font-semibold">Depus</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB]">
              {data.rows.map((r) => {
                const st = rowState(r);
                const canOpen = ["submitted", "verified", "draft", "not_submitted"].includes(r.state);
                return (
                  <React.Fragment key={r.enrollment_id}>
                    <tr>
                      <td className="px-4 py-2.5 font-semibold text-[#373A45]">
                        {r.name}{r.attempt > 1 && <span className="ml-2 text-xs font-normal text-[#6B7280]">retransmitere</span>}
                        {r.invalidated && <span className="block text-xs font-normal text-[#8a5d12]">modificat după semnare</span>}
                      </td>
                      <td className="px-3 py-2.5"><Chip tone={st.tone}>{st.label}</Chip></td>
                      <td className="px-3 py-2.5 text-xs text-[#6B7280]">{r.submitted_at ? whenLabel(r.submitted_at) : "—"}</td>
                      <td className="px-4 py-2.5 text-right">
                        {team && r.resubmit?.status === "requested" && (
                          <ApproveResubmit groupId={groupId} eid={r.enrollment_id} onDone={() => { setKind("resit"); load(); }} />
                        )}
                        {canOpen && (
                          <button type="button" className={quiet} onClick={() => setOpen(open === r.enrollment_id ? null : r.enrollment_id)}
                            data-testid={`pf-open-${r.enrollment_id}`}>
                            {open === r.enrollment_id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                            {r.state === "submitted" ? "Verifică" : "Vezi"}
                          </button>
                        )}
                      </td>
                    </tr>
                    {open === r.enrollment_id && (
                      <tr><td colSpan={4} className="bg-[#FAFAFB] px-4 py-4">
                        <Detail base={base} groupId={groupId} eid={r.enrollment_id} team={team} checks={data.checks}
                          slots={data.slots} onSaved={load} />
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {data.rows.length === 0 && <p className="px-4 py-4 text-sm text-[#6B7280]">Nimeni în sesiunea aceasta.</p>}
        </div>
      </section>

      <Annex6 base={base} groupId={groupId} kind={kind} data={data} onChanged={setData} />
    </div>
  );
}

function DeadlineEditor({ session, groupId, kind, defaultDays, onDone }) {
  const [days, setDays] = useState(String(session.portfolio_days));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/admin/portal/groups/${groupId}/portfolio-deadline`, { session: kind, days: Number(days), reason });
      toast.success(r.data.told ? `Termenul e schimbat; i-am anunțat pe ${r.data.told} cursanți.` : "Termenul e schimbat.");
      setReason("");
      onDone();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut schimba termenul."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-3 grid gap-2 rounded-xl bg-white p-3 text-sm ring-1 ring-[#E5E7EB] sm:grid-cols-[auto_1fr_auto] sm:items-center"
      data-testid="pf-deadline-editor">
      <label className="flex items-center gap-2 text-[#373A45]">
        Portofoliul se predă la
        <input className="w-16 rounded-lg border border-[#E5E7EB] px-2 py-1 text-sm tabular-nums" inputMode="numeric" value={days}
          onChange={(e) => setDays(e.target.value)} aria-label="Zile după ultima zi de formare" />
        zile <span className="text-xs text-[#6B7280]">(implicit {defaultDays}, cel mult 10)</span>
      </label>
      <input className={field} value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder="Motivul (obligatoriu după prima sesiune; cursanții îl primesc pe email)" aria-label="Motivul schimbării" />
      <button type="button" className={quiet} disabled={busy || String(session.portfolio_days) === days} onClick={save}>Schimbă</button>
    </div>
  );
}

function ApproveResubmit({ groupId, eid, onDone }) {
  const [busy, setBusy] = useState(false);
  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/portal/groups/${groupId}/portfolios/${eid}/resubmit-approve`);
      toast.success("Retransmiterea e aprobată: cursantul trece în sesiunea de restanță.");
      onDone();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut aproba."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" className={`${quiet} mr-2`} disabled={busy} onClick={approve} data-testid={`pf-approve-${eid}`}>
      Aprobă retransmiterea
    </button>
  );
}

function Detail({ base, groupId, eid, team, checks, slots, onSaved }) {
  const [d, setD] = useState(null);
  const [answers, setAnswers] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const fileBase = team ? "/admin/portal" : "/portal";

  const apply = (x) => {
    setD(x);
    setAnswers(x.structure_is_current ? checksFrom(x.structure) : {});
    setNote(x.structure_is_current ? x.structure.note || "" : "");
  };
  useEffect(() => {
    api.get(`${base}/groups/${groupId}/portfolios/${eid}`).then((r) => apply(r.data))
      .catch((err) => toast.error(apiErrorMessage(err, "Nu am putut deschide portofoliul.")));
  }, [base, groupId, eid]);

  if (!d) return <Loader2 className="animate-spin text-[#6B7280]" />;
  const grid = gridState(answers, checks.length);
  const save = async () => {
    setBusy(true);
    try {
      apply((await api.post(`${base}/groups/${groupId}/portfolios/${eid}/structure`, { checks: answers, note })).data);
      toast.success("Am salvat verificarea structurii.");
      onSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut salva."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-5 text-sm text-[#373A45]" data-testid="pf-detail">
      {!d.seal && <p className="text-[#8a5d12]">{d.invalidated ? "Portofoliul a fost modificat după semnare și nu e depus din nou." : "Portofoliul nu e depus."}</p>}
      {d.seal && (
        <>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]">Ce s-a depus</p>
            <ul className="mt-2 grid gap-2">
              {slots.map((slot) => {
                const got = d.seal.slots?.[slot.key] || { files: [], links: [] };
                return (
                  <li key={slot.key} className="rounded-xl bg-white p-3 ring-1 ring-[#E5E7EB]">
                    <p className="font-semibold">{slot.label}</p>
                    {!got.files.length && !got.links.length && <p className="text-xs text-[#6B7280]">gol</p>}
                    {got.files.map((f) => (
                      <button key={f.file_id} type="button" className="mt-1 flex items-center gap-1.5 text-left hover:text-[#965EA7]"
                        onClick={() => downloadFile(f.file_id, (m) => toast.error(m), fileBase)}>
                        <Paperclip size={13} /> {f.name} <span className="font-mono text-[10px] text-[#6B7280]">sha256 {f.sha256.slice(0, 12)}…</span>
                      </button>
                    ))}
                    {got.links.map((ln, i) => (
                      <a key={i} href={ln.url} target="_blank" rel="noopener noreferrer" className="mt-1 flex items-center gap-1.5 text-[#965EA7] hover:underline">
                        <Link2 size={13} /> {ln.label || ln.url}
                      </a>
                    ))}
                  </li>
                );
              })}
            </ul>
            <p className="mt-2"><strong>Instrumentele AI:</strong> {d.seal.ai_tools || "—"}</p>
            <p className="mt-1 text-xs text-[#6B7280]">
              Semnat de {d.seal.signature.full_name} la {whenLabel(d.seal.at)} · declarația din {d.seal.signature.declaration_version} ·
              cod {d.seal.code}
              {team && d.seal.signature.ip && ` · IP ${d.seal.signature.ip} · ${d.seal.signature.user_agent || "browser necunoscut"}`}
              {d.seals.length > 1 && ` · ${d.seals.length} depuneri în total`}
            </p>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]">Grila de verificare a structurii</p>
            <p className="mt-1 text-xs text-[#6B7280]">ADMIS doar dacă toate elementele sunt DA. Lângă fiecare, ce a găsit platforma.</p>
            <ol className="mt-2 grid gap-2">
              {checks.map((c) => {
                const h = d.hints[c.n] || d.hints[String(c.n)] || {};
                const v = answers[String(c.n)];
                return (
                  <li key={c.n} className="grid gap-1 rounded-xl bg-white p-3 ring-1 ring-[#E5E7EB] sm:grid-cols-[1fr_auto] sm:items-center">
                    <div>
                      <p><strong>{c.n}.</strong> {c.label}</p>
                      <p className={`text-xs ${h.warn ? "font-semibold text-[#8a5d12]" : "text-[#6B7280]"}`}>{h.text}</p>
                    </div>
                    <div className="inline-flex overflow-hidden rounded-full border border-[#E5E7EB]" role="radiogroup" aria-label={`Elementul ${c.n}`}>
                      {[[true, "DA"], [false, "NU"]].map(([val, label]) => (
                        <button key={label} type="button" role="radio" aria-checked={v === val}
                          onClick={() => setAnswers({ ...answers, [String(c.n)]: val })}
                          className={`px-3 py-1 text-xs font-semibold ${v === val ? (val ? "bg-[#519872] text-white" : "bg-[#c0483f] text-white") : "hover:bg-[#F5F6F6]"}`}
                          data-testid={`pf-check-${c.n}-${label}`}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </li>
                );
              })}
            </ol>
            <textarea className={`${field} mt-3`} rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder={grid.verdict === "respins" ? "Ce lipsește (obligatoriu la respins; cursantul primește observația)" : "Observații (opțional)"}
              aria-label="Observații" />
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button type="button" className={primary} disabled={busy || !grid.complete || (grid.verdict === "respins" && !note.trim())}
                onClick={save} data-testid="pf-structure-save">
                {busy && <Loader2 size={14} className="animate-spin" />} Salvează: {grid.verdict ? grid.verdict.toUpperCase() : "…"}
              </button>
              {d.structure && d.structure_is_current && (
                <span className="text-xs text-[#6B7280]">Salvat ca {d.structure.verdict} de {d.structure.by?.name}, {whenLabel(d.structure.at)}.</span>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Annex6({ base, groupId, kind, data, onChanged }) {
  const [registry, setRegistry] = useState({ no: "", date: "" });
  const [busy, setBusy] = useState(false);
  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${base}/groups/${groupId}/portfolio-documents`, {
        session: kind, registry_no: registry.no, registry_date: registry.date });
      toast.success(`Anexa 6, versiunea ${r.data.document.version}, e generată.`);
      onChanged(r.data);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut genera Anexa 6."));
    } finally {
      setBusy(false);
    }
  };
  const versions = data.documents.filter((d) => d.scope === kind);
  return (
    <section aria-labelledby="pf-annex6">
      <h2 id="pf-annex6" className="font-display text-xl font-bold text-[#373A45]">Anexa 6</h2>
      <p className="mt-1 text-sm text-[#6B7280]">
        Catalogul centralizator de verificare a structurii portofoliilor: admis sau respins pentru transmiterea portofoliului
        de evaluare finală. Cine nu a depus în termen apare respins. Generată, nu se mai schimbă.
      </p>
      <div className={`mt-3 grid gap-3 p-4 ${card}`} data-testid="pf-annex6">
        {!data.annex_6.ready && (
          <ul className="list-disc pl-5 text-xs text-[#8a5d12]">{data.annex_6.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
        )}
        <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-center">
          <input className={field} value={registry.no} onChange={(e) => setRegistry({ ...registry, no: e.target.value })}
            placeholder="Nr. de înregistrare (opțional)" aria-label="Numărul din registru" />
          <input className={field} value={registry.date} onChange={(e) => setRegistry({ ...registry, date: e.target.value })}
            placeholder="din data (opțional)" aria-label="Data din registru" />
          <button type="button" className={quiet} disabled={busy || !data.annex_6.ready} onClick={generate} data-testid="pf-annex6-generate">
            {busy && <Loader2 size={13} className="animate-spin" />} Generează Anexa 6
          </button>
        </div>
        {versions.map((d) => (
          <div key={d.id} className="flex flex-wrap items-center gap-2 text-xs">
            <span className={d.latest ? "font-semibold" : "text-[#6B7280]"}>{versionLine(d)}{d.registry_no ? ` · nr. ${d.registry_no}` : ""}</span>
            {/* As on the other annexes (19.10.2026): a check or a submission moved under it. */}
            {d.signed && <Chip tone="good">semnată</Chip>}
            {d.latest && d.stale && <Chip tone="warn">datele s-au schimbat</Chip>}
            {["pdf", "xlsx"].map((fmt) => (
              <button key={fmt} type="button" className="inline-flex items-center gap-1 font-semibold text-[#965EA7] hover:underline"
                onClick={() => downloadBlob(`${base}/documents/${d.id}/${fmt}`,
                  `Anexa_6_${asciiName(data.group.name)}_${kind}_v${d.version}.${fmt}`)}>
                <Download size={12} /> {fmt === "pdf" ? "PDF" : "Excel"}
              </button>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}
